export class ApiError extends Error {
  constructor(
    public override name: string,
    message: string,
    public contexts: {
      request: Record<string, unknown>;
      response: Record<string, unknown>;
    },
  ) {
    super(message);
  }

  /** The backend error code (e.g. `ALMCONNECTION_COMMON__WRONG_PASSPHRASE_ERROR`),
   * surfaced top-level so adapters can map typed errors by `err.code`. */
  get code(): string | undefined {
    return (this.contexts.response as { code?: string }).code;
  }
}

/** A pubsub event whose publisher role the subscription does not accept.
 * Typed so `onHandleError` can sample or de-dup the drops by `instanceof`. */
export class ForbiddenPublisherError extends ApiError {
  constructor(role: string, subject: string) {
    super("ForbiddenPublisherError", `role '${role}' may not publish ${subject}`, {
      request: { subject },
      response: {},
    });
  }
}

export class TimeoutError extends ApiError {
  constructor(contexts: { request: Record<string, unknown>; response: Record<string, unknown> }) {
    super("TimeoutError", "API call timed out", contexts);
  }
}

export class NoSuccessError extends ApiError {
  constructor(
    message: string,
    endpoint: string,
    contexts: { request: Record<string, unknown>; response: NoSuccessResponse },
  ) {
    super(`NoSuccessError: ${message}`, `Server call unsuccessful to ${endpoint}`, contexts);
  }

  get response(): NoSuccessResponse {
    return this.contexts.response as NoSuccessResponse;
  }

  /** The backend error code (e.g. `ALMCONNECTION_COMMON__WRONG_PASSPHRASE_ERROR`),
   * surfaced top-level so adapters can map typed errors by `err.code`. */
  get code(): string | undefined {
    return this.response.code;
  }

  /** The human-readable backend message. `Error.message` is the generic
   * "Server call unsuccessful to …", so expose the real message here — toast
   * helpers (e.g. showFeedback) prefer `userMessage` over `message`. */
  get userMessage(): string {
    return this.response.message;
  }
}

interface NoSuccessResponse extends Record<string, unknown> {
  message: string;
  code?: string;
  details?: unknown;
}
